import * as Yup from 'yup';
import { isValidPhoneNumber } from 'libphonenumber-js';
import {
  dateTodayOrAfter,
  formatDates,
  salesforceCountyOptionsByCountry,
  validatePostCode,
} from '../../helpers/helpers';
import useCountries from '../../helpers/useCountries';
import countries from '../../helpers/hardcodedCountries';

const allSameChars = /^(.)\1*$/;
const allSymbols = /^[-!$%^&*()_+|~=`{}[\]:";'<>?,./]*$/;
const allNumbers = /^[0-9]*$/;
const allSpaces = /^[\s]*$/;
const nameAllowedChars = /^[0-9a-zA-ZÀ-ÖØ-öø-ÿ\- —.;:&?@()"'!+/\\]*$/;

const validationSchema = Yup.object({
  organisationName: Yup.string()
    .min(3, 'validation.moreThan3Chars')
    .max(50, 'validation.lessThan50Chars')
    .required('validation.fieldRequired')
    .test('is-valid', 'validation.hasInvalidChars', (value) => nameAllowedChars.test(value))
    .test('not-all-same-chars', 'validation.notAllSameChars', (value) => !allSameChars.test(value))
    .test('not-all-symbols', 'validation.notAllSymbols', (value) => !allSymbols.test(value))
    .test('not-all-numbers', 'validation.notAllNumbers', (value) => !allNumbers.test(value))
    .test('not-all-spaces', 'validation.notAllSpaces', (value) => !allSpaces.test(value)),
  industryType: Yup.string().required('validation.fieldRequired'),
  businessType: Yup.string().required('validation.fieldRequired'),
  startTradingDate: Yup.string()
    .test('is-valid', 'validation.dateGreaterThanToday', (value) =>
      dateTodayOrAfter(formatDates(new Date(value), new Date()))
    )
    .required('validation.fieldRequired'),
  phoneNumber: Yup.string()
    .required('validation.fieldRequired')
    .test(
      'is-valid',
      'validation.invalidPhoneNumber',
      (value) => value && isValidPhoneNumber(value)
    ),
  addressLine1: Yup.string()
    .required('validation.fieldRequired')
    .max(100, 'validation.lessThan100Chars'),
  addressLine2: Yup.string().max(50, 'validation.lessThan50Chars'),
  town: Yup.string().required('validation.fieldRequired').max(50, 'validation.lessThan50Chars'),
  county: Yup.string()
    .max(50, 'validation.lessThan50Chars')
    .test('county', 'validation.fieldRequired', function (value) {
      const { country } = this.parent;
      const newCountyList = salesforceCountyOptionsByCountry(country);

      return (newCountyList?.length > 0 && value !== '') || newCountyList.length < 1;
    }),
  country: Yup.string().required('validation.fieldRequired').max(50, 'validation.lessThan50Chars'),
  postCode: Yup.string()
    .required('validation.fieldRequired')
    .test('postCode', 'validation.invalidPostCode', function (value) {
      const { country } = this.parent;
      return validatePostCode(value, country);
    }),
  trust: Yup.object().when('businessType', {
    is: 10,
    then: (schema) =>
      schema.shape({
        trusteeType: Yup.string().required('validation.fieldRequired'),
        trusteeFirstName: Yup.string().when('trusteeType', {
          is: 1,
          then: (subSchema) =>
            subSchema
              .min(3, 'validation.moreThan3Chars')
              .max(50, 'validation.lessThan50Chars')
              .required('validation.fieldRequired')
              .test('is-valid', 'validation.hasInvalidChars', (value) =>
                nameAllowedChars.test(value)
              )
              .test(
                'not-all-same-chars',
                'validation.notAllSameChars',
                (value) => !allSameChars.test(value)
              )
              .test(
                'not-all-symbols',
                'validation.notAllSymbols',
                (value) => !allSymbols.test(value)
              )
              .test(
                'not-all-numbers',
                'validation.notAllNumbers',
                (value) => !allNumbers.test(value)
              )
              .test('not-all-spaces', 'validation.notAllSpaces', (value) => !allSpaces.test(value)),
          otherwise: (subSchema) => subSchema.notRequired(),
        }),
        trusteeLastName: Yup.string().when('trusteeType', {
          is: 1,
          then: (subSchema) =>
            subSchema
              .min(3, 'validation.moreThan3Chars')
              .max(50, 'validation.lessThan50Chars')
              .required('validation.fieldRequired')
              .test('is-valid', 'validation.hasInvalidChars', (value) =>
                nameAllowedChars.test(value)
              )
              .test(
                'not-all-same-chars',
                'validation.notAllSameChars',
                (value) => !allSameChars.test(value)
              )
              .test(
                'not-all-symbols',
                'validation.notAllSymbols',
                (value) => !allSymbols.test(value)
              )
              .test(
                'not-all-numbers',
                'validation.notAllNumbers',
                (value) => !allNumbers.test(value)
              )
              .test('not-all-spaces', 'validation.notAllSpaces', (value) => !allSpaces.test(value)),
          otherwise: (subSchema) => subSchema.notRequired(),
        }),
        trusteeLegalName: Yup.string().when('trusteeType', {
          is: 2,
          then: (subSchema) =>
            subSchema
              .min(3, 'validation.moreThan3Chars')
              .max(50, 'validation.lessThan50Chars')
              .required('validation.fieldRequired')
              .test('is-valid', 'validation.hasInvalidChars', (value) =>
                nameAllowedChars.test(value)
              )
              .test(
                'not-all-same-chars',
                'validation.notAllSameChars',
                (value) => !allSameChars.test(value)
              )
              .test(
                'not-all-symbols',
                'validation.notAllSymbols',
                (value) => !allSymbols.test(value)
              )
              .test(
                'not-all-numbers',
                'validation.notAllNumbers',
                (value) => !allNumbers.test(value)
              )
              .test('not-all-spaces', 'validation.notAllSpaces', (value) => !allSpaces.test(value)),
          otherwise: (subSchema) => subSchema.notRequired(),
        }),
        name: Yup.string()
          .min(3, 'validation.moreThan3Chars')
          .max(50, 'validation.lessThan50Chars')
          .required('validation.fieldRequired')
          .test('is-valid', 'validation.hasInvalidChars', (value) => nameAllowedChars.test(value))
          .test(
            'not-all-same-chars',
            'validation.notAllSameChars',
            (value) => !allSameChars.test(value)
          )
          .test('not-all-symbols', 'validation.notAllSymbols', (value) => !allSymbols.test(value))
          .test('not-all-numbers', 'validation.notAllNumbers', (value) => !allNumbers.test(value))
          .test('not-all-spaces', 'validation.notAllSpaces', (value) => !allSpaces.test(value)),
        type: Yup.string().required('validation.fieldRequired'),
      }),
    otherwise: (schema) => schema.transform(() => undefined),
  }),
});

export default validationSchema;
